if (!window.customPatternMediaLoaded) {
  window.customPatternMediaLoaded = true;

  class CustomPatternMedia {
    constructor(productInfo) {
      this.productInfo = productInfo;
      this.gallery = productInfo.querySelector('media-gallery');
      this.mappingElement = this.gallery?.querySelector('[data-custom-pattern-media-map]');
      this.handleThumbnailClick = this.handleThumbnailClick.bind(this);

      if (!this.gallery || !this.mappingElement) return;

      try {
        this.mapping = JSON.parse(this.mappingElement.textContent);
      } catch (error) {
        console.error('Unable to parse the custom Pattern media mapping.', error);
        return;
      }

      this.enabled = this.mapping.variants.some((variant) => variant.mediaIds.length > 0);
      if (!this.enabled) {
        this.initialized = true;
        return;
      }

      this.variantsById = new Map();
      this.variantIdByMediaId = new Map();

      this.mapping.variants.forEach((variant) => {
        const variantId = String(variant.id);
        const mediaIds = variant.mediaIds.map(String);

        this.variantsById.set(variantId, { ...variant, id: variantId, mediaIds });
        mediaIds.forEach((mediaId) => {
          if (!this.variantIdByMediaId.has(mediaId)) this.variantIdByMediaId.set(mediaId, variantId);
        });
      });

      const variantMediaOrder = this.mapping.variants.flatMap((variant) => variant.mediaIds.map(String));
      const productMediaOrder = (this.mapping.mediaOrder || []).map(String);
      this.customMediaOrder = [...new Set([...variantMediaOrder, ...productMediaOrder])];

      if (variantMediaOrder.length > 0) this.gallery.classList.add('custom-pattern-media--enabled');

      // Run after Studio's thumbnail button handler so any native carousel
      // movement is captured and preserved through the variant refresh.
      this.gallery.addEventListener('click', this.handleThumbnailClick);
      this.applyVariant(this.mapping.selectedVariantId);
      this.initialized = true;
    }

    getThumbnailList() {
      return this.gallery.querySelector('[id^="Slider-Thumbnails-"]');
    }

    getViewerList() {
      return this.gallery.querySelector('[id^="Slider-Gallery-"]');
    }

    getViewerItems() {
      return Array.from(this.gallery.querySelectorAll('[id^="Slider-Gallery-"] > li[data-media-id]'));
    }

    getThumbnailItems() {
      return Array.from(this.getThumbnailList()?.querySelectorAll(':scope > li[data-target]') || []);
    }

    mediaIdFromCompoundId(compoundId) {
      return String(compoundId || '').split('-').pop();
    }

    mediaIdFromViewerItem(item) {
      return this.mediaIdFromCompoundId(item.dataset.mediaId);
    }

    mediaIdFromThumbnailItem(item) {
      return this.mediaIdFromCompoundId(item.dataset.target);
    }

    orderDesktopThumbnails(items) {
      const list = this.getThumbnailList();
      if (!list || !window.matchMedia('(min-width: 750px)').matches) return;
      if (this.gallery.dataset.desktopLayout !== 'thumbnail_slider') return;

      this.orderItems(list, items, this.mediaIdFromThumbnailItem);
    }

    orderMobileGallery(viewerItems, thumbnailItems) {
      if (!window.matchMedia('(max-width: 749px)').matches) return;

      this.orderItems(this.getViewerList(), viewerItems, this.mediaIdFromViewerItem);
      this.orderItems(this.getThumbnailList(), thumbnailItems, this.mediaIdFromThumbnailItem);
    }

    orderItems(list, items, getMediaId) {
      if (!list) return;

      const itemByMediaId = new Map(items.map((item) => [getMediaId.call(this, item), item]));
      const currentMediaIds = items.map((item) => getMediaId.call(this, item));
      const orderedMediaIds = [...this.customMediaOrder, ...currentMediaIds];
      const insertedMediaIds = new Set();

      orderedMediaIds.forEach((mediaId) => {
        if (insertedMediaIds.has(mediaId)) return;
        const item = itemByMediaId.get(mediaId);
        if (!item) return;

        list.append(item);
        insertedMediaIds.add(mediaId);
      });
    }

    applyVariant(variantId, preferredMediaId, preservedThumbnailScrollLeft) {
      const variant = this.variantsById.get(String(variantId));
      const highlightedMediaIds = variant?.mediaIds || [];
      const highlightedSet = new Set(highlightedMediaIds);
      this.gallery.classList.toggle('custom-pattern-media--has-selection', highlightedMediaIds.length > 0);

      const viewerItems = this.getViewerItems();
      viewerItems.forEach((item) => {
        item.classList.toggle('custom-pattern-media--highlighted', highlightedSet.has(this.mediaIdFromViewerItem(item)));
      });

      const thumbnailItems = this.getThumbnailItems();
      thumbnailItems.forEach((item) => {
        item.classList.toggle(
          'custom-pattern-media--highlighted',
          highlightedSet.has(this.mediaIdFromThumbnailItem(item))
        );
      });
      this.orderDesktopThumbnails(thumbnailItems);
      this.orderMobileGallery(viewerItems, thumbnailItems);

      this.gallery.querySelector('[id^="GalleryViewer-"]')?.resetPages?.();
      this.gallery.querySelector('[id^="GalleryThumbnails-"]')?.resetPages?.();

      const activeMediaId = highlightedMediaIds.includes(String(preferredMediaId))
        ? String(preferredMediaId)
        : highlightedMediaIds[0];
      if (activeMediaId) {
        this.gallery.setActiveMedia?.(`${this.productInfo.dataset.section}-${activeMediaId}`, false);
        this.scrollThumbnailIntoView(activeMediaId, preservedThumbnailScrollLeft);
      }
    }

    scrollThumbnailIntoView(mediaId, preservedScrollLeft) {
      if (!window.matchMedia('(min-width: 750px)').matches) return;
      if (this.gallery.dataset.desktopLayout !== 'thumbnail_slider') return;

      window.cancelAnimationFrame(this.thumbnailScrollFrame);
      this.thumbnailScrollFrame = window.requestAnimationFrame(() => {
        this.thumbnailScrollFrame = window.requestAnimationFrame(() => {
          const thumbnailList = this.getThumbnailList();
          const thumbnail = this.getThumbnailItems().find(
            (item) => this.mediaIdFromThumbnailItem(item) === String(mediaId)
          );
          if (!thumbnailList || !thumbnail) return;

          // A thumbnail click already established the customer's intended
          // viewport. Studio's variant refresh temporarily prepends featured
          // media, so restore the pre-refresh position afterward.
          if (Number.isFinite(preservedScrollLeft)) {
            thumbnailList.scrollTo({ left: preservedScrollLeft, behavior: 'auto' });
            return;
          }

          const thumbnailLeft = thumbnail.offsetLeft;
          const thumbnailRight = thumbnailLeft + thumbnail.clientWidth;
          const visibleLeft = thumbnailList.scrollLeft;
          const visibleRight = visibleLeft + thumbnailList.clientWidth;

          // Preserve the customer's current carousel position when the newly
          // selected thumbnail is already fully visible.
          if (thumbnailLeft >= visibleLeft && thumbnailRight <= visibleRight) return;

          thumbnailList.scrollTo({ left: Math.max(0, thumbnailLeft), behavior: 'smooth' });
        });
      });
    }

    handleVariantChange(variant) {
      if (!this.enabled || !variant?.id) return;

      const preferredMediaId = this.pendingActiveMediaId;
      const preservedThumbnailScrollLeft = this.pendingThumbnailScrollLeft;
      this.pendingActiveMediaId = null;
      this.pendingThumbnailScrollLeft = null;
      this.applyVariant(variant.id, preferredMediaId, preservedThumbnailScrollLeft);
    }

    handleThumbnailClick(event) {
      if (!window.matchMedia('(min-width: 750px)').matches) return;

      const thumbnail = event.target.closest('li[data-target]');
      if (!thumbnail || !this.gallery.contains(thumbnail)) return;

      const mediaId = this.mediaIdFromThumbnailItem(thumbnail);
      const variantId = this.variantIdByMediaId.get(mediaId);
      if (!variantId) return;

      const currentVariantId = this.productInfo.querySelector('form[action*="/cart/add"] input[name="id"]')?.value;
      if (String(currentVariantId) === variantId) return;

      const variant = this.variantsById.get(variantId);
      if (variant) {
        this.pendingActiveMediaId = mediaId;
        this.pendingThumbnailScrollLeft = this.getNativeThumbnailScrollPosition(thumbnail);
        this.productInfo.setAttribute('data-custom-preserve-active-media', 'true');
        this.productInfo.setAttribute('data-custom-skip-option-focus', 'true');
        if (!this.selectPattern(variant.pattern)) {
          this.pendingActiveMediaId = null;
          this.pendingThumbnailScrollLeft = null;
          this.productInfo.removeAttribute('data-custom-preserve-active-media');
          this.productInfo.removeAttribute('data-custom-skip-option-focus');
        }
      }
    }

    getNativeThumbnailScrollPosition(thumbnail) {
      const thumbnailList = this.getThumbnailList();
      if (!thumbnailList) return null;

      const visibleLeft = thumbnailList.scrollLeft;
      const visibleRight = visibleLeft + thumbnailList.clientWidth - 10;
      const thumbnailLeft = thumbnail.offsetLeft;
      const thumbnailRight = thumbnailLeft + thumbnail.clientWidth;
      const isFullyVisible = thumbnailLeft >= visibleLeft && thumbnailRight <= visibleRight;

      return isFullyVisible ? visibleLeft : thumbnailLeft;
    }

    selectPattern(pattern) {
      const variantSelects = this.productInfo.querySelector('variant-selects');
      if (!variantSelects) return false;

      const matchingInput = Array.from(variantSelects.querySelectorAll('[data-option-name]')).find(
        (input) => input.dataset.optionName.toLowerCase() === 'pattern' && input.value === pattern
      );
      if (!matchingInput) return false;

      if (matchingInput.tagName === 'OPTION') {
        const select = matchingInput.closest('select');
        if (!select || select.value === pattern) return false;
        select.value = pattern;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      } else if (matchingInput.matches('input[type="radio"]') && !matchingInput.checked) {
        matchingInput.checked = true;
        matchingInput.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }

      return false;
    }
  }

  const customPatternMediaInstances = new WeakMap();

  const initializeCustomPatternMedia = (root = document) => {
    const productInfos = root.matches?.('product-info') ? [root] : root.querySelectorAll?.('product-info') || [];

    productInfos.forEach((productInfo) => {
      if (customPatternMediaInstances.has(productInfo)) return;
      const instance = new CustomPatternMedia(productInfo);
      if (instance.initialized) customPatternMediaInstances.set(productInfo, instance);
    });
  };

  document.addEventListener('DOMContentLoaded', () => initializeCustomPatternMedia());
  document.addEventListener('shopify:section:load', (event) => initializeCustomPatternMedia(event.target));
  document.addEventListener('product-info:loaded', (event) => initializeCustomPatternMedia(event.target));

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      document.querySelectorAll('product-info').forEach((productInfo) => {
        if (String(productInfo.dataset.section) !== String(data.sectionId)) return;
        customPatternMediaInstances.get(productInfo)?.handleVariantChange(data.variant);
      });
    });
  }
}
